/**
 * The pack hall's lists in the site list's shape, built from the server's pack endpoints. The
 * site list (GET /process-runs/site-pack) carries no pack runs, pack lots or buyers, so the V1 hall
 * screens read them once online and keep them with the site list for offline use:
 *
 *   GET /pack-runs?locationId=   runs allocated to this hall (params: sku, planned kilos, pouches)
 *   GET /skus/{id}               the product (pack size, carton, shelf life)
 *   GET /label-versions?skuId=&status=APPROVED   the approved label (start takes the one there is)
 *   GET /pack-lots               lots of this hall's runs (code, dates, QR link, release, hold)
 *   GET /orgs?kind=BUYER         customers
 *
 * PURE: the mapping from server rows is unit-tested (hallData.test.ts).
 */
import type { PackLot, PackRun, SitePack } from '@/offline/sitePack'
import { RUN_STATUSES, type RunStatus } from '@/offline/sitePack'

type Row = Record<string, unknown>

const isRow = (v: unknown): v is Row => typeof v === 'object' && v !== null && !Array.isArray(v)
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)
const num = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}
const day = (v: unknown): string | null => {
  const s = str(v) ?? (v instanceof Date ? v.toISOString() : null)
  return s ? s.slice(0, 10) : null
}
const iso = (v: unknown): string | null => str(v) ?? (v instanceof Date ? v.toISOString() : null)

/** A list answer: an array, or `{ data: [] }`. */
export function rowsOf(data: unknown): Row[] {
  const list = Array.isArray(data) ? data : isRow(data) && Array.isArray(data.data) ? data.data : []
  return list.filter(isRow)
}

export interface ServerSku {
  id: string
  code: string
  name: string
  crop_id: string
  crop_name: string | null
  net_g: number
  tolerance_g: number
  pouches_per_carton: number
  shelf_life_days: number | null
  gtin: string | null
}

export function skuFromServer(r: Row): ServerSku | null {
  const id = str(r.id)
  const net = num(r.net_g ?? r.netG)
  if (!id || !net) return null
  return {
    id,
    code: str(r.code) ?? id,
    name: str(r.name) ?? str(r.code) ?? id,
    crop_id: str(r.crop_id ?? r.cropId) ?? '',
    crop_name: str(r.crop_name),
    net_g: Math.round(net),
    tolerance_g: Math.round(num(r.tolerance_g ?? r.toleranceG) ?? 0),
    pouches_per_carton: Math.max(
      1,
      Math.round(num(r.pouches_per_carton ?? r.pouchesPerCarton) ?? 1),
    ),
    shelf_life_days: num(r.shelf_life_days ?? r.shelfLifeDays),
    gtin: str(r.gtin),
  }
}

export function labelFromServer(r: Row): PackRun['label_versions'][number] | null {
  const id = str(r.id)
  if (!id) return null
  const mrp = num(r.mrp_inr)
  return {
    id,
    code: str(r.code) ?? id,
    status: str(r.status) ?? 'APPROVED',
    mrp_paise: mrp === null ? null : Math.round(mrp * 100),
    allergen_statement: str(r.allergen_statement),
    claim_text: str(r.claim_text),
    fssai_licence_no: str(r.fssai_licence_no),
    fssai_licence_valid_to: str(r.fssai_licence_valid_to),
  }
}

export function packLotFromServer(r: Row): PackLot | null {
  const id = str(r.id)
  const code = str(r.code)
  const packDate = day(r.pack_date)
  if (!id || !code || !packDate) return null
  const hold = str(r.hold)
  return {
    id,
    code,
    run_id: str(r.run_id),
    sku_id: str(r.sku_id),
    sku_name: str(r.sku_name),
    net_g: num(r.net_g),
    pack_date: packDate,
    best_before: day(r.best_before) ?? packDate,
    qr_url: str(r.qr_url) ?? '',
    status: str(r.status) ?? 'PACKING',
    hold: hold === 'HELD' || hold === 'RECALLED' ? hold : 'NONE',
    hold_reason: str(r.hold_reason),
    pouch_count: num(r.pouch_count),
    pouches_dispatched: num(r.pouches_dispatched) ?? 0,
    released_at: iso(r.released_at),
    label_version_code: str(r.label_code ?? r.label_version_code),
  }
}

export interface Buyer {
  id: string
  name: string
  code: string | null
  /** The town under the name (when the org row has one). */
  place: string | null
}

export function buyerFromServer(r: Row): Buyer | null {
  const id = str(r.id)
  const name = str(r.name)
  if (!id || !name) return null
  const addr = isRow(r.address) ? r.address : {}
  return {
    id,
    name,
    code: str(r.code),
    place: str(r.city) ?? str(r.district) ?? str(addr.city) ?? str(addr.district) ?? null,
  }
}

/** The buyers kept with the site list (`buyers`, loose rows). */
export function buyersOf(pack: Pick<SitePack, 'buyers'>): Buyer[] {
  return pack.buyers.map((b) => ({
    id: b.id,
    name: b.name,
    code: b.code ?? null,
    place: typeof b.place === 'string' ? b.place : null,
  }))
}

export interface SiteFacts {
  /** Batch unit → its farmer's roster number and hold, from the site list's sacks. */
  batches: Map<
    string,
    { farmer_code: string | null; hold: 'NONE' | 'QUARANTINE' | 'HELD' | 'RECALLED' }
  >
}

const status = (v: unknown): RunStatus => {
  const s = str(v)
  return s && (RUN_STATUSES as readonly string[]).includes(s) ? (s as RunStatus) : 'OPEN'
}

/**
 * One pack run of the hall in the site list's shape. `prev` is the run started before it on the
 * same line (its product's crop decides the full allergen clean, as the server does).
 */
export function packRunFromServer(
  r: Row,
  sku: ServerSku,
  labels: PackRun['label_versions'],
  lot: PackLot | null,
  prev: { crop_id: string; crop_name: string | null } | null,
  facts: SiteFacts,
): PackRun | null {
  const id = str(r.id)
  const code = str(r.code)
  const batch = str(r.origin_unit_id ?? r.batch_unit_id)
  if (!id || !code || !batch) return null
  const params = isRow(r.params) ? r.params : {}
  const plannedKg = num(params.planned_kg)
  const plannedG = plannedKg === null ? 0 : Math.round(plannedKg * 1000)
  const approved = labels.filter((l) => l.status === 'APPROVED')
  const label = approved[0] ?? null
  const fact = facts.batches.get(batch)
  return {
    id,
    code,
    status: status(r.status),
    equipment_id: str(r.equipment_id),
    line_name: str(r.equipment_code),
    operator_id: str(r.operator_id),
    batch_unit_id: batch,
    batch_code: str(r.batch_code) ?? '',
    crop_id: sku.crop_id,
    crop_name: sku.crop_name,
    farmer_code: fact?.farmer_code ?? null,
    batch_hold: fact?.hold ?? 'NONE',
    sku: {
      id: sku.id,
      code: sku.code,
      name: sku.name,
      net_g: sku.net_g,
      tolerance_g: sku.tolerance_g,
      pouches_per_carton: sku.pouches_per_carton,
      shelf_life_days: sku.shelf_life_days,
      gtin: sku.gtin,
    },
    planned_g: plannedG,
    planned_pouches: Math.round(
      num(params.planned_pouches) ?? (sku.net_g > 0 ? Math.floor(plannedG / sku.net_g) : 0),
    ),
    planned_date: day(params.planned_date) ?? day(r.created_at) ?? '',
    label_versions: approved,
    label_version_id: str(params.label_version_id) ?? label?.id ?? null,
    previous_crop_id: prev?.crop_id ?? null,
    previous_crop_name: prev?.crop_name ?? null,
    allergen_clean_required: !!prev && prev.crop_id !== sku.crop_id,
    premises_licence_no: label?.fssai_licence_no ?? null,
    premises_licence_valid_to:
      typeof label?.fssai_licence_valid_to === 'string' ? label.fssai_licence_valid_to : null,
    started_at: iso(r.started_at),
    pack_lot: lot,
    issued_container_ids: [],
    spot_every: 50,
    bundle_at: null,
    inputs: [],
    outputs: [],
    balance: null,
  }
}

/** The run started just before `run` on the same line (null: the first on this line). */
export function previousOnLine(rows: readonly Row[], run: Row): Row | null {
  const line = str(run.equipment_id)
  const at = iso(run.started_at) ?? iso(run.created_at) ?? '9999'
  let best: Row | null = null
  for (const r of rows) {
    if (r === run || str(r.id) === str(run.id) || str(r.equipment_id) !== line) continue
    const s = iso(r.started_at)
    if (!s || s >= at || str(r.status) === 'CANCELLED') continue
    if (!best || s > (iso(best.started_at) ?? '')) best = r
  }
  return best
}

/** Facts about the batches at this site, from the site list's sacks and lots. */
export function siteFacts(pack: Pick<SitePack, 'bags' | 'lots'>): SiteFacts {
  const batches: SiteFacts['batches'] = new Map()
  for (const u of pack.lots) batches.set(u.id, { farmer_code: u.farmer_code ?? null, hold: u.hold })
  for (const b of pack.bags) {
    const cur = batches.get(b.unit_id)
    const code = typeof b.farmer_code === 'string' ? b.farmer_code : null
    batches.set(b.unit_id, {
      farmer_code: cur?.farmer_code ?? code,
      hold: cur && cur.hold !== 'NONE' ? cur.hold : b.hold,
    })
  }
  return { batches }
}

/** Run ids the hall keeps: live ones, and the ones closed in the last 14 days. */
export function keepRun(r: Row, now: number): boolean {
  const s = str(r.status)
  if (s === 'CANCELLED') return false
  if (s === 'OPEN' || s === 'RUNNING') return true
  const ended = iso(r.ended_at)
  return !!ended && now - Date.parse(ended) < 14 * 86_400_000
}
